import React, { useEffect, useMemo, useState } from "react";
import AgGridTable from "../components/table/AgGridTable";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import { ColDef } from "ag-grid-community";
import axiosInstance from "../axios/axios";
import Cisco_img from "../assets/cisco-assets.svg";

const NistResourcesTable = ({ rowid }: any) => {
  const [isrowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `/securityscan/compliance-assets?control_id=${rowid}&module=cloud`
        );
        setRowData(response.data.data.compliance_assets);
      } catch (error) {
        console.error("Error fetching controls:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [rowid]);

  const rowData = isrowData?.map((control) => ({
    assets: control.hostname,
    vendor: control.vendor,
    device_type: control.device_type,
    type: `${control.vendor} - ${control.device_type}`,
    compliance: control.compliance,
    percentage:
      control.recommendations > 0
        ? (control.passed_recommendations / control.recommendations) * 100
        : 0,
    passed_recommendations: control.passed_recommendations,
    recommendations: control.recommendations,
  }));
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  const columnDefs: ColDef[] = [
    {
      field: "assets",
      headerName: "Name",
      minWidth: 300,
      // valueGetter: (params: any) => params.node.rowIndex + 1,
    },

    {
      field: "type",
      headerName: "Type",
      filter: true,
      minWidth: 200,
      cellRenderer: (params: any) => {
        const vendor = params.data.vendor;
        const deviceType = params.data.device_type;
        const isCisco = vendor?.toLowerCase() === "cisco";
        const typeLabel = `${vendor} - ${deviceType}`;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isCisco && (
              <img
                src={Cisco_img}
                alt="Cisco"
                style={{ width: 30, height: 30 }}
              />
            )}
            <Typography variant="body2">{typeLabel}</Typography>
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },

    {
      field: "compliance",
      headerName: "Compliance",
      filter: true,
      minWidth: 50,
      cellRenderer: (params: any) => {
        const rawValue = Number(params.value) || 0;
        const value = Math.round(rawValue);

        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 30,
                height: 30,
              }}
            >
              {/* Background circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{ color: "#c5c5d2" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {String(value) + "%"}
            </Typography>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      // cellRenderer: (params: any) => {
      //   const isManual = params.value === "Manual";
      //   const color = isManual ? "#F7941F" : "#696595";

      //   return (
      //     <div
      //       style={{
      //         border: `1px solid ${color}`,
      //         borderRadius: 4,
      //         padding: "2px 6px",
      //         lineHeight: "1.5",
      //         whiteSpace: "nowrap",
      //         fontSize: 12,
      //         color: color,
      //       }}
      //     >
      //       {params.value}
      //     </div>
      //   );
      // },
    },

    {
      field: "percentage",
      headerName: "Benchmark",
      filter: true,
      minWidth: 100,
      cellRenderer: (params: any) => {
        const passedCount = params.data.passed_recommendations;
        const compliance_count = params.data.recommendations;
        const progressValue = params.data.percentage || 0;

        return (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#f5f5f5",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        progressValue >= 70
                          ? "#4caf50"
                          : progressValue >= 40
                            ? "#ff9800"
                            : "#f44336",
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {`${passedCount}/${compliance_count}`}
              </Typography>
            </Box>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <div
      style={{
        height: "calc(75vh - 95px)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        scrollbarWidth: "none",
      }}
    >
      <AgGridTable
        columnDefs={columnDefs}
        styles={agGridInlineStyles}
        data={rowData}
        showSearch
        isRowSelectable
        height={"calc(72vh - 110px)"}
        isLoading={isLoading}
      />
    </div>
  );
};

export default NistResourcesTable;
